import { KeyedObject } from '@trendiolive/trendio-selfcare-ui-kit';

import axios, { AxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { REFRESH_TOKEN } from 'queries/refresh_token';

// TODO: Improve API service, it cannot be a utility - monday 18232921603
const axiosGraphQLServices = axios.create({ baseURL: import.meta.env.VITE_TRENDIO_API_URL || 'http://localhost:3010/' });

const axiosAIRestServices = axios.create({
  baseURL: import.meta.env.VITE_AI_BACKEND_URL || 'http://localhost:8000',
  headers: {
    'Content-Type': 'application/json'
  }
});

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosGraphQLServices.get(url, { ...config });

  return res.data;
};

export const fetcherPost = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosGraphQLServices.post(url, { ...config });

  return res.data;
};

export const verifyToken: (st: string) => boolean = (serviceToken) => {
  if (!serviceToken) {
    return false;
  }
  const decoded: KeyedObject = jwtDecode(serviceToken);
  /* Refresh token every hour */
  return decoded.exp > Date.now() / 1000 + 1380 * 60;
};

export const setSession = (token?: string | null) => {
  if (token) {
    localStorage.setItem('token', token);
    axiosGraphQLServices.defaults.headers.common.Authorization = `JWT ${token}`;
    axiosAIRestServices.defaults.headers.common.Authorization = `JWT ${token}`;
  } else {
    localStorage.removeItem('token');
    delete axiosGraphQLServices.defaults.headers.common.Authorization;
    delete axiosAIRestServices.defaults.headers.common.Authorization;
  }
};

const authenticateWithToken = async () => {
  const token = localStorage.getItem('token');
  if (token) {
    if (verifyToken(token)) {
      setSession(token);
    } else {
      const response = await refreshToken(token);
      if (response.data.tokenRefresh?.token) {
        setSession(response.data.tokenRefresh.token);
      } else {
        setSession(null);
        return response.data;
      }
    }
  }
};

export const refreshToken = async (token: string) => {
  const response = await axiosGraphQLServices.post(import.meta.env.VITE_TRENDIO_API_URL, { query: REFRESH_TOKEN, variables: { token } });
  return response.data;
};

export const authenticatedPost = async ({ query, vars, abortController }: AxiosQueryProps) => {
  await authenticateWithToken();
  const response = await axiosGraphQLServices.post(
    import.meta.env.VITE_TRENDIO_API_URL,
    { query, variables: vars },
    { signal: abortController?.signal }
  );
  return response.data;
};

export const authenticatedMultipartPost = async ({
  query,
  vars,
  file,
  fileMapPath,
  abortController
}: AxiosQueryProps & { file: File; fileMapPath: string }) => {
  await authenticateWithToken();
  const formData = new FormData();
  formData.append(
    'operations',
    JSON.stringify({
      query,
      variables: vars
    })
  );
  formData.append('map', JSON.stringify({ '0': [fileMapPath] }));
  formData.append('0', file);
  const response = await axiosGraphQLServices.post(import.meta.env.VITE_TRENDIO_API_URL, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    signal: abortController?.signal
  });
  return response.data;
};

export const unauthenticatedPost = async ({ query, vars, abortController }: AxiosQueryProps) => {
  const response = await axiosGraphQLServices.post(
    import.meta.env.VITE_TRENDIO_API_URL,
    { query, variables: vars },
    { signal: abortController?.signal }
  );
  return response.data;
};

export const authenticatedAIGet = async (endpoint: string) => {
  await authenticateWithToken();
  const response = await axiosAIRestServices.get(endpoint);
  return response.data;
};

export const authenticatedAIPost = async (endpoint: string, data: unknown) => {
  await authenticateWithToken();
  const response = await axiosAIRestServices.post(endpoint, data);
  return response.data;
};

export const authenticatedAIPatch = async (endpoint: string, data: unknown) => {
  await authenticateWithToken();
  const response = await axiosAIRestServices.patch(endpoint, data);
  return response.data;
};

export type AxiosQueryProps = {
  query: string;
  vars?: unknown;
  abortController?: AbortController;
};
